<script setup lang="ts">
import type { BlockGallery, BlockGalleryFile, BlockButtonGroup, File, ProjectGalleryResult } from '~/types';

const props = defineProps<{
  data: BlockGallery;
}>();

const galleryItems = computed(() => {
  return props.data.gallery_items?.map((item: BlockGalleryFile) => {
    return item.directus_files_id as File;
  });
});

// The block's selected file IDs (used to reverse-map files → project posts).
const blockFileIds = computed(() => {
  return (props.data.gallery_items ?? [])
    .map((item: BlockGalleryFile) => {
      const file = item.directus_files_id;
      if (!file) return undefined;
      return typeof file === 'string' ? file : file.id;
    })
    .filter((id): id is string => !!id);
});

// Resolve the block's files into project cards. Falls back to `mode: 'file'`
// when nothing resolves (e.g., a plain file gallery with no project mapping).
const { data: projectGallery } = await useAsyncData(
  `block-gallery-${props.data.id ?? 'unknown'}`,
  () => useProjectGallery(blockFileIds.value, { showAllProjects: props.data.show_all_projects ?? false }),
  { watch: [] },
);

const projectResult = computed<ProjectGalleryResult | null>(() => projectGallery.value ?? null);

const showProjectGallery = computed(() => {
  return projectResult.value?.mode === 'project' && (projectResult.value?.cards?.length ?? 0) > 0;
});
</script>

<template>
  <BlockContainer>
    <TypographyTitle v-if="data.title">{{ data.title }}</TypographyTitle>
    <TypographyHeadline v-if="data.headline" :content="data.headline" size="lg" />
    <VProjectGallery v-if="showProjectGallery" :cards="projectResult!.cards" />
    <VGallery
      v-else-if="galleryItems && galleryItems.length > 0"
      :items="galleryItems"
      :group_by_category="data.group_by_category ?? undefined"
    />
    <div v-if="data.add_button == 'show_a_button'" class="mt-10">
      <BlocksButtonGroup v-if="data.button_group" :data="data.button_group as BlockButtonGroup" />
    </div>
  </BlockContainer>
</template>
